// ============================================================
// TaskAssigneesField.tsx — the task "Assign to" field (create, edit and the
// My work reassign popover).
//
// One ordered list of people. The FIRST is the LEAD (Task.assignedToUserId —
// due-today alerts, edit alerts, team load, Group by Owner); the rest are
// co-assignees (TaskAssignee rows). The lead is badged "Lead"; another person
// can be promoted with "Make lead" (the old lead stays on as a co-assignee).
// A separate "Everyone in the firm can see it" switch is the firm-wide flag.
//
// Gating (UX — the backend is the real gate):
//   • adding / removing people        → canPeople ("Assign tasks to others")
//   • Make lead                        → canChangeLead (admin / current lead /
//                                         creator) AND canPeople (the old lead
//                                         is kept as a co-assignee row)
//   • remove the LEAD (×)              → the same as Make lead; one action that
//                                         promotes the NEXT person to lead and
//                                         takes the old lead off (one save: the
//                                         lead change is audited + emails the
//                                         new lead, as any lead change)
//   • firm-wide switch                 → canFirm
//   The lead can't be removed while they're the only person: Task.assignedToUserId
//   is required, so a task always has a lead (even a firm-wide one).
// Removing someone who'd be left unable to see the task asks first
// (lossWarning → ConfirmDialogue).
// ============================================================
import React, { useState } from 'react'
import UserAvatar from '../shared/UserAvatar'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import type { FirmUser } from '../../types/user.types'
import type { Task } from '../../types/tasks.types'

export interface TaskAssignState {
  leadId: string
  others: string[] // co-assignees, lead excluded, in order
  firmWide: boolean
}

// Stored task → field state. TaskAssignee rows may include the lead (creator
// auto-include); the field shows them once, lead first.
export const assignStateOf = (t: Pick<Task, 'assignedToUserId' | 'firmWide' | 'assignees'>): TaskAssignState => ({
  leadId: t.assignedToUserId,
  others: (t.assignees ?? []).map((a) => a.userId).filter((id, i, arr) => id !== t.assignedToUserId && arr.indexOf(id) === i),
  firmWide: !!t.firmWide,
})

// Field state → the save fragment. Only the fields this user may change, so
// the backend's diff-gate is never tripped by an un-permitted field. With
// co-assignees the rows hold EVERYONE on it (lead included); a lone lead → no rows.
export const assignPayload = (
  s: TaskAssignState,
  canPeople: boolean,
  canFirm: boolean,
): { assignedToUserId: string; firmWide?: boolean; assigneeUserIds?: string[] } => ({
  assignedToUserId: s.leadId,
  ...(canFirm ? { firmWide: s.firmWide } : {}),
  ...(canPeople ? { assigneeUserIds: coOf(s).length ? [s.leadId, ...coOf(s)] : [] } : {}),
})

// Co-assignees without the lead (a remote lead change can briefly list the
// new lead in both places).
const coOf = (s: TaskAssignState): string[] => s.others.filter((id) => id !== s.leadId)

const nameOf = (u: FirmUser | undefined): string =>
  u ? (`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || 'Unknown') : 'Former member'

interface Props {
  value: TaskAssignState
  onChange: (next: TaskAssignState) => void
  canPeople: boolean
  canFirm: boolean
  canChangeLead: boolean
  disabled?: boolean
  firmUsers: FirmUser[]
  currentUserId: string
  /** Text for the "remove?" confirm when taking `userId` off would leave them
   *  unable to see the task; null = remove without asking. */
  lossWarning?: (userId: string, next: TaskAssignState) => string | null
  compact?: boolean
}

const TaskAssigneesField: React.FC<Props> = ({
  value, onChange, canPeople, canFirm, canChangeLead, disabled = false, firmUsers, currentUserId, lossWarning, compact = false,
}) => {
  const [pendingRemove, setPendingRemove] = useState<{ message: string; next: TaskAssignState } | null>(null)
  const byId = new Map(firmUsers.map((u) => [u.id, u]))
  const others = coOf(value)
  const people = [value.leadId, ...others]
  const editable = !disabled
  const addable = firmUsers.filter((u) => u.isActive && !people.includes(u.id))

  // Removing the lead promotes the next person; anyone else just leaves.
  const remove = (id: string): void => {
    const next = id === value.leadId
      ? { ...value, leadId: others[0], others: others.slice(1) }
      : { ...value, others: others.filter((x) => x !== id) }
    const warn = lossWarning?.(id, next) ?? null
    if (warn) { setPendingRemove({ message: warn, next }); return }
    onChange(next)
  }
  const canRemoveLead = editable && canChangeLead && canPeople && others.length > 0
  const makeLead = (id: string): void => {
    onChange({ ...value, leadId: id, others: [value.leadId, ...others.filter((x) => x !== id)] })
  }

  return (
    <div>
      <ul className="flex flex-col gap-1" aria-label="Assigned people">
        {people.map((id, i) => {
          const u = byId.get(id)
          const lead = i === 0
          return (
            <li key={id} className={`flex items-center gap-2 rounded-lg border border-casevo-border ${compact ? 'px-2 py-1' : 'px-2.5 py-1.5'} bg-casevo-surface`}>
              <UserAvatar userId={id} fallbackName={nameOf(u)} size="xs" title={false} />
              <span className="flex-1 min-w-0 truncate text-[13px] text-casevo-forest">
                {nameOf(u)}{id === currentUserId ? ' (you)' : ''}
              </span>
              {lead && (
                <span className="shrink-0 text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-casevo-primary text-white" title="Lead — gets the due-date and edit alerts and is shown as the task's owner">
                  Lead
                </span>
              )}
              {!lead && editable && canChangeLead && canPeople && (
                <button type="button" onClick={() => makeLead(id)}
                  className="shrink-0 text-[11.5px] font-semibold text-casevo-mid hover:text-casevo-forest underline underline-offset-2 focus:outline-none casevo-focus rounded">
                  Make lead
                </button>
              )}
              {(lead ? canRemoveLead : editable && canPeople) && (
                <button type="button" onClick={() => remove(id)}
                  aria-label={lead ? `Remove ${nameOf(u)} — ${nameOf(byId.get(others[0]))} becomes lead` : `Remove ${nameOf(u)}`}
                  title={lead ? `Remove — ${nameOf(byId.get(others[0]))} becomes lead` : undefined}
                  className="shrink-0 w-6 h-6 inline-flex items-center justify-center rounded text-casevo-mid hover:text-casevo-danger hover:bg-casevo-mint/30 focus:outline-none casevo-focus">
                  <span aria-hidden="true">×</span>
                </button>
              )}
            </li>
          )
        })}
      </ul>
      {editable && canPeople && addable.length > 0 && (
        <select value="" aria-label="Add a person"
          onChange={(e) => { const id = e.target.value; if (id) onChange({ ...value, others: [...others, id] }) }}
          className="mt-1.5 w-full border border-casevo-border rounded px-3 py-2 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid">
          <option value="">+ Add a person…</option>
          {addable.map((u) => (
            <option key={u.id} value={u.id}>{nameOf(u)}{u.id === currentUserId ? ' (you)' : ''}</option>
          ))}
        </select>
      )}
      {editable && canFirm ? (
        <label className="mt-2 flex items-center gap-2 text-[12.5px] text-casevo-forest cursor-pointer">
          <input type="checkbox" checked={value.firmWide} onChange={(e) => onChange({ ...value, firmWide: e.target.checked })} className="accent-casevo-primary" />
          Everyone in the firm can see it
        </label>
      ) : value.firmWide && (
        <p className="mt-1.5 text-[11.5px] text-casevo-mid">Everyone in the firm can see this task.</p>
      )}
      {editable && !canPeople && (
        <p className="mt-1.5 text-[11.5px] text-casevo-mid">Assigning tasks to other people needs the “Assign tasks to others” permission.</p>
      )}
      <ConfirmDialogue
        isOpen={!!pendingRemove}
        title="Remove from this task?"
        message={pendingRemove?.message ?? ''}
        confirmLabel="Remove"
        onConfirm={() => {
          if (pendingRemove) onChange(pendingRemove.next)
          setPendingRemove(null)
        }}
        onCancel={() => setPendingRemove(null)}
      />
    </div>
  )
}

export default TaskAssigneesField
